import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Impressum() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://physio-muehlbacher.at';

    document.title = 'Impressum – Physiotherapie Theresa Mühlbacher | Munderfing';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Impressum der Physiotherapiepraxis Theresa Mühlbacher, Achenlohe 13, 5222 Munderfing. Inhaberin: Theresa Mühlbacher, Gerichtsstand Mattighofen.'
      );
    }

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/impressum`,
      url: `${siteUrl}/impressum`,
      name: 'Impressum – Physiotherapie Theresa Mühlbacher',
      description:
        'Impressum der Physiotherapiepraxis Theresa Mühlbacher in Munderfing. Inhaberin: Theresa Mühlbacher, Achenlohe 13, 5222 Munderfing, Gerichtsstand Mattighofen.',
      inLanguage: 'de-AT',
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      publisher: {
        '@type': 'Person',
        name: 'Theresa Mühlbacher',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Achenlohe 13',
          addressLocality: 'Munderfing',
          postalCode: '5222',
          addressCountry: 'AT',
        },
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Startseite',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Impressum',
          item: `${siteUrl}/impressum`,
        },
      ],
    };

    const schemas = [webPageSchema, breadcrumbSchema];
    const scriptTags: HTMLScriptElement[] = schemas.map((schema, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `schema-impressum-${i}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      scriptTags.forEach((el) => {
        if (document.head.contains(el)) document.head.removeChild(el);
      });
      document.title =
        'Physiotherapie Munderfing – Theresa Mühlbacher | Rücken, Gelenk & Beckenbodentherapie';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="bg-olive-900 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gold-300 text-sm font-medium mb-8 hover:text-gold-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Zurück zur Startseite
          </Link>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-white mb-4">
            Impressum
          </h1>
          <div className="w-16 h-px bg-gold-300"></div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">

        {/* Angaben gemäß § 25 MedienG */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            Angaben gemäß § 25 MedienG
          </h2>
          <div className="space-y-3 text-olive-700 leading-relaxed">
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">Inhaberin:</span>
              <span className="sm:col-span-2">Theresa Mühlbacher</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">Adresse:</span>
              <span className="sm:col-span-2">
                Achenlohe 13<br />
                5222 Munderfing<br />
                Österreich
              </span>
            </div>
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">Telefon:</span>
              <span className="sm:col-span-2">
                <a href="tel:+436603450403" className="hover:text-gold-600 transition-colors">
                  +43 660 3450403
                </a>
              </span>
            </div>
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">E-Mail:</span>
              <span className="sm:col-span-2">
                <a href="mailto:Theresa.weiss1991@gmail.com" className="hover:text-gold-600 transition-colors">
                  Theresa.weiss1991@gmail.com
                </a>
              </span>
            </div>
          </div>
        </section>

        {/* Berufsrecht */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            Berufsrechtliche Angaben
          </h2>
          <div className="space-y-3 text-olive-700 leading-relaxed">
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">Berufsbezeichnung:</span>
              <span className="sm:col-span-2">Physiotherapeutin</span>
            </div>

          </div>
        </section>

        {/* Gerichtsstand */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            Gerichtsstand
          </h2>
          <p className="text-olive-700 leading-relaxed">
            Gerichtsstand ist <strong>Mattighofen</strong>, Österreich.
          </p>
        </section>

        {/* Haftungsausschluss */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            Haftungsausschluss
          </h2>
          <div className="space-y-6 text-olive-700 leading-relaxed">
            <div>
              <h3 className="font-semibold text-olive-900 mb-2">Haftung für Inhalte</h3>
              <p>
                Die Inhalte dieser Website wurden mit größtmöglicher Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte kann jedoch keine Gewähr übernommen werden. Als Diensteanbieter bin ich gemäß den gesetzlichen Vorschriften für eigene Inhalte auf diesen Seiten verantwortlich.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-olive-900 mb-2">Haftung für Links</h3>
              <p>
                Diese Website enthält Links zu externen Webseiten Dritter, auf deren Inhalte kein Einfluss genommen werden kann. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber verantwortlich. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-olive-900 mb-2">Urheberrecht</h3>
              <p>
                Die durch die Seitenbetreiberin erstellten Inhalte und Werke auf dieser Website unterliegen dem österreichischen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung der jeweiligen Autorin.
              </p>
            </div>
          </div>
        </section>

        {/* Streitbeilegung */}
        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            Online-Streitbeilegung
          </h2>
          <p className="text-olive-700 leading-relaxed">
            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
            <a
              href="https://ec.europa.eu/consumers/odr/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-gold-600 hover:underline"
            >
              https://ec.europa.eu/consumers/odr/
            </a>
            . Ich bin nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
          </p>
        </section>

        <div className="border-t border-olive-100 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-olive-900 text-white px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-olive-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Zurück zur Startseite
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-olive-900 text-white/60 text-sm py-6 px-6 mt-4">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
          <p>© 2025 Physiotherapie Theresa Mühlbacher. Alle Rechte vorbehalten.</p>
          <div className="flex gap-5">
            <Link to="/impressum" className="hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
              Impressum
            </Link>
            <Link to="/datenschutz" className="hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
              Datenschutzerklärung
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
